// Minimal ZIP writer using the STORED method (no compression).
//
// Receipt audit packages bundle CSV + PDF + image/PDF receipts. Images and
// PDFs are already compressed, so DEFLATE would add ~5% size at best while
// pulling in a 50KB dependency (jszip). STORED is sufficient and ~150 lines.
//
// Format reference: APPNOTE.TXT 6.3.10 (PKWARE) — local file headers,
// central directory, end-of-central-directory record. CRC-32 per IEEE 802.3.

interface ZipEntry {
  name: string;
  data: Uint8Array;
  crc32: number;
  // DOS-style date+time (2-second resolution, no timezone). Receipts don't
  // need wall-clock fidelity for audit; we stamp "now".
  dosDate: number;
  dosTime: number;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(d: Date): { date: number; time: number } {
  // Bits per APPNOTE 4.4.6:
  //   date: yyyyyyy m mmm ddddd  (year-1980, 4-bit month, 5-bit day)
  //   time: hhhhh mmmmmm sssss   (hour, minute, second/2)
  const year = Math.max(1980, d.getFullYear()) - 1980;
  const date = (year << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  const time =
    (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  return { date, time };
}

function utf8(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function le16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

function le32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

export interface ZipInput {
  name: string;
  data: Uint8Array | string;
}

export function buildZip(inputs: ZipInput[]): Uint8Array {
  const now = new Date();
  const dt = dosDateTime(now);
  const entries: ZipEntry[] = inputs.map((i) => {
    const data = typeof i.data === "string" ? utf8(i.data) : i.data;
    return {
      name: i.name,
      data,
      crc32: crc32(data),
      dosDate: dt.date,
      dosTime: dt.time,
    };
  });

  // First pass: compute layout (offsets + total size).
  // Local header is 30 bytes + name length + data length per entry.
  // Central directory entry is 46 bytes + name length per entry.
  // End-of-central-directory record is 22 bytes.
  const localHeaders: { offset: number; size: number }[] = [];
  let cursor = 0;
  for (const e of entries) {
    const nameBytes = utf8(e.name).length;
    const size = 30 + nameBytes + e.data.length;
    localHeaders.push({ offset: cursor, size });
    cursor += size;
  }
  const centralStart = cursor;
  let centralSize = 0;
  for (const e of entries) {
    const nameBytes = utf8(e.name).length;
    centralSize += 46 + nameBytes;
  }
  const totalSize = centralStart + centralSize + 22;

  const out = new Uint8Array(totalSize);
  const view = new DataView(out.buffer);

  // Pass 2: emit local headers + data.
  cursor = 0;
  for (const e of entries) {
    const nameBytes = utf8(e.name);
    le32(view, cursor, 0x04034b50); // local file header signature
    le16(view, cursor + 4, 20); // version needed (2.0)
    le16(view, cursor + 6, 1 << 11); // flags: bit 11 = UTF-8 names
    le16(view, cursor + 8, 0); // compression method 0 = stored
    le16(view, cursor + 10, e.dosTime);
    le16(view, cursor + 12, e.dosDate);
    le32(view, cursor + 14, e.crc32);
    le32(view, cursor + 18, e.data.length); // compressed size = uncompressed
    le32(view, cursor + 22, e.data.length);
    le16(view, cursor + 26, nameBytes.length);
    le16(view, cursor + 28, 0); // extra field length
    out.set(nameBytes, cursor + 30);
    out.set(e.data, cursor + 30 + nameBytes.length);
    cursor += 30 + nameBytes.length + e.data.length;
  }

  // Pass 3: emit central directory.
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i]!;
    const lh = localHeaders[i]!;
    const nameBytes = utf8(e.name);
    le32(view, cursor, 0x02014b50); // central directory header signature
    le16(view, cursor + 4, 20); // version made by
    le16(view, cursor + 6, 20); // version needed
    le16(view, cursor + 8, 1 << 11); // flags
    le16(view, cursor + 10, 0); // method
    le16(view, cursor + 12, e.dosTime);
    le16(view, cursor + 14, e.dosDate);
    le32(view, cursor + 16, e.crc32);
    le32(view, cursor + 20, e.data.length);
    le32(view, cursor + 24, e.data.length);
    le16(view, cursor + 28, nameBytes.length);
    le16(view, cursor + 30, 0); // extra
    le16(view, cursor + 32, 0); // comment length
    le16(view, cursor + 34, 0); // disk number
    le16(view, cursor + 36, 0); // internal attrs
    le32(view, cursor + 38, 0); // external attrs
    le32(view, cursor + 42, lh.offset); // local header offset
    out.set(nameBytes, cursor + 46);
    cursor += 46 + nameBytes.length;
  }

  // Pass 4: emit end-of-central-directory.
  le32(view, cursor, 0x06054b50);
  le16(view, cursor + 4, 0); // disk
  le16(view, cursor + 6, 0); // disk with central directory
  le16(view, cursor + 8, entries.length); // entries on this disk
  le16(view, cursor + 10, entries.length); // total entries
  le32(view, cursor + 12, centralSize);
  le32(view, cursor + 16, centralStart);
  le16(view, cursor + 20, 0); // comment length

  return out;
}
